import { firstBy } from "thenby";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedVisionUser,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import {
  calculateActions,
  defaultActions,
  ParticipantRoleSchema,
  tk,
} from "@thalamos/vision-common";

import type { IncidentTableRow } from "../../../components/IncidentWorkItems/IncidentTable/types.js";
import { extractIncidentDisplayValues } from "../../../components/IncidentWorkItems/IncidentWorkItemCard/extractIncidentDisplayValues.js";
import type { useIncidentActions } from "../useIncidentActions.js";

/**
 * Build the rows for the incident table based on the incidents and user information.
 */
export function buildRows({
  mode,
  user,
  incidents,
  isPendingTransfer,
  canCancelTransfer,
  handleViewIncident,
  handleSecondaryAction,
  t,
}: {
  mode: "myIncidents" | "transferredIncidents";
  user: ExtendedVisionUser | undefined;
  incidents: IncidentWorkItemWithContext[];
  isPendingTransfer: ReturnType<typeof useIncidentActions>["isPendingTransfer"];
  canCancelTransfer: ReturnType<typeof useIncidentActions>["canCancelTransfer"];
  handleViewIncident: ReturnType<
    typeof useIncidentActions
  >["handleViewIncident"];
  handleSecondaryAction: ReturnType<
    typeof useIncidentActions
  >["handleSecondaryAction"];
  t: (key: string, options?: Record<string, unknown>) => string;
}): IncidentTableRow[] {
  return incidents
    .toSorted(
      firstBy((incident) => incident.incidentWorkItem.createdAt, "desc"),
    )
    .map((incident) => {
      const matchingParticipant = incident.participants.find(
        (p) => p.participantId === user?.id,
      );
      const isPending = isPendingTransfer(matchingParticipant);
      const canCancel = canCancelTransfer(matchingParticipant);

      const actions = user
        ? calculateActions({
            incidentWorkItem: incident,
            user,
            asOfDateTime: dayjs(),
          })
        : defaultActions;

      const displayValues = extractIncidentDisplayValues(incident, t);

      return {
        id: incident.incidentWorkItem.id,
        ...displayValues,
        isPendingTransfer: isPending,
        primaryButton: {
          label: isPending
            ? tk("incidentWorkItem.acceptTransfer")
            : tk("incidentWorkItem.view"),
          disabled:
            matchingParticipant?.role !== ParticipantRoleSchema.enum.owner &&
            !actions.pdf.canView &&
            mode === "transferredIncidents",
          onClick: () => {
            void handleViewIncident(incident, matchingParticipant);
          },
        },
        ...(canCancel || isPending
          ? {
              secondaryButton: {
                label: isPending
                  ? tk("incidentWorkItem.reject")
                  : tk("incidentWorkItem.cancel"),
                onClick: async () => {
                  await handleSecondaryAction(incident, matchingParticipant);
                },
              },
            }
          : {}),
      };
    });
}
